import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { backendUrl } from '../../config/api';
import Pagination from '../UI/Pagination';
import styles from './RoomTable.module.css';

axios.defaults.withCredentials = true;

const C = {
    card:   'rgba(13,19,50,0.65)',
    border: 'rgba(255,255,255,0.08)',
    cyan:   '#00d4ff',
    amber:  '#ffb300',
    green:  '#00c853',
};

export default function RoomTable({ hideHeader = false }) {
    const [rooms,      setRooms]      = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [error,      setError]      = useState(null);
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 10 });
    const [joinCode,   setJoinCode]   = useState('');

    const fetchRooms = useCallback(async (page = 1, limit = pagination.limit) => {
        setLoading(true); setError(null);
        try {
            const res = await axios.get(`${backendUrl}/api/rooms/open`, { params: { page, limit } });
            setRooms(res.data.rooms ?? []);
            const pag = res.data.pagination ?? {};
            setPagination({ page: pag.page || 1, pages: pag.pages || 1, total: pag.total || 0, limit: pag.limit || limit });
        } catch (e) {
            setError('Failed to load rooms');
        } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.limit]);

    useEffect(() => { fetchRooms(1, 10); }, []);  // eslint-disable-line

    const handleJoin = () => {
        const code = joinCode.trim().toUpperCase();
        if (!code) return;
        window.location.href = `/join/${code}`;
    };

    return (
        <div className={styles.container}>
            {!hideHeader && (
                <div className={styles.header}>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                        🎮 Open Rooms
                        <span style={{ marginLeft: 8, fontSize: 11, color: 'rgba(255,255,255,0.35)', fontWeight: 400 }}>{pagination.total} available</span>
                    </div>
                    <div className={styles.controls}>
                        <input
                            placeholder="Room code…"
                            value={joinCode}
                            onChange={e => setJoinCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8))}
                            onKeyDown={e => e.key === 'Enter' && handleJoin()}
                            className={styles.joinInput}
                        />
                        <button
                            onClick={handleJoin}
                            className={styles.joinBtn}
                        >Join</button>
                        <button
                            onClick={() => fetchRooms(pagination.page, pagination.limit)}
                            className={styles.refreshBtn}
                        >↻</button>
                    </div>
                </div>
            )}

            {error && (
                <div style={{ padding: '10px 18px', fontSize: 12, color: '#ff6b6b', background: 'rgba(255,59,48,0.08)', borderBottom: `1px solid rgba(255,59,48,0.15)` }}>
                    ⚠ {error}
                </div>
            )}

            {loading ? (
                <div style={{ padding: '48px 0', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: 13 }}>
                    <div style={{ width: 28, height: 28, border: '2px solid rgba(0,212,255,0.2)', borderTop: `2px solid ${C.cyan}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                    Loading rooms…
                </div>
            ) : rooms.length === 0 ? (
                <div style={{ padding: '48px 0', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
                    <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.4 }}>🎲</div>
                    No open rooms right now
                </div>
            ) : (
                <>
                    <div className={styles.tableScroll}>
                        <table className={styles.table}>
                            <thead>
                                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                                    {['Room', 'Mode', 'Players', 'Access', ''].map(h => (
                                        <th key={h} style={{
                                            padding: '10px 14px', textAlign: 'left',
                                            fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)',
                                            textTransform: 'uppercase', letterSpacing: 1,
                                            background: 'rgba(0,0,0,0.1)', whiteSpace: 'nowrap',
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rooms.map((r, idx) => (
                                    <tr
                                        key={r.roomId}
                                        style={{
                                            borderBottom: `1px solid rgba(255,255,255,0.04)`,
                                            background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.03)'}
                                        onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)'}
                                    >
                                        <td style={{ padding: '10px 14px' }}>
                                            <div style={{ fontFamily: 'monospace', fontWeight: 700, color: C.amber, fontSize: 13, letterSpacing: '0.1em' }}>
                                                {r.roomCode || r.roomId.slice(0, 8).toUpperCase()}
                                            </div>
                                            {r.name && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 1 }}>{r.name}</div>}
                                        </td>
                                        <td style={{ padding: '10px 14px', color: C.cyan, fontWeight: 600 }}>
                                            {r.gameMode === 'timer' ? `timer • ${r.timerMinutes || 2}m` : (r.gameMode || 'classic')}
                                        </td>
                                        <td style={{ padding: '10px 14px', color: 'rgba(255,255,255,0.7)' }}>
                                            <span style={{ fontWeight: 700, color: r.playerCount >= r.maxPlayers ? '#ff6b6b' : C.green }}>
                                                {r.playerCount}/{r.maxPlayers}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 14px' }}>
                                            {r.hasPassword
                                                ? <span style={{ fontSize: 11, color: 'rgba(255,179,0,0.8)', fontWeight: 600 }}>🔒 Private</span>
                                                : <span style={{ fontSize: 11, color: 'rgba(0,200,83,0.8)', fontWeight: 600 }}>🌐 Public</span>
                                            }
                                        </td>
                                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                                            <button
                                                onClick={() => window.location.href = `/join/${r.roomCode || r.roomId}`}
                                                className={styles.rowActionBtn}
                                            >Join</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.border}` }}>
                        <Pagination
                            page={pagination.page}
                            pages={pagination.pages}
                            total={pagination.total}
                            limit={pagination.limit}
                            onPage={p => fetchRooms(p, pagination.limit)}
                            onLimit={l => { setPagination(prev => ({ ...prev, limit: l })); fetchRooms(1, l); }}
                        />
                    </div>
                </>
            )}
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
    );
}
